if (!window.Eurus.loadedScript.has("quiz.js")) {
  window.Eurus.loadedScript.add("quiz.js");

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xQuiz", (sectionId, maxProduct, defaultProductIds, maxArticle) => ({
        loading: false,
        hasProducts: true,
        hasArticles: false,
        totalQuestions: 0,
        curIndex: 0,
        questionIds: [],
        questionSkipIds: [],
        traversed: [],
        currentQuestionId: null,
        answers: new Map(),
        skips: new Map(),
        selectedOptions: new Map(),

        init() {
          const questions = this.$el.querySelectorAll(".quiz-question");
          const startButton = this.$el.querySelector(
            `.button-quiz-${sectionId} button`
          );
          this.totalQuestions = questions.length;
          this.$nextTick(() => {
            this.questionIds = Array.from(questions).map(
              (q) => q.dataset.blockId
            );
            this.questionSkipIds = Array.from(questions).map(
              (q) => q.dataset.questionId
            );
            startButton.disabled = false;
          });
        },

        _getSkippedIds() {
          const skipped = new Set();
          for (const ids of this.skips.values()) {
            for (const id of ids) skipped.add(id);
          }
          return skipped;
        },

        _getNextQuestionId(fromBlockId) {
          const skipped = this._getSkippedIds();
          const pos = fromBlockId ? this.questionIds.indexOf(fromBlockId) : -1;
          for (let i = pos + 1; i < this.questionIds.length; i++) {
            if (!skipped.has(this.questionSkipIds[i]))
              return this.questionIds[i];
          }
          return null;
        },

        _getPaginationWidth() {
          if (this.curIndex === 0) return 0;
          if (!this.currentQuestionId) return 100;
          const skipped = this._getSkippedIds();
          const pos = this.questionIds.indexOf(this.currentQuestionId);
          const remaining = this.questionSkipIds
            .slice(pos + 1)
            .filter((skipId) => !skipped.has(skipId)).length;
          const effectiveTotal = this.traversed.length + 1 + remaining;
          return effectiveTotal > 0
            ? ((this.traversed.length + 1) / (effectiveTotal + 1)) * 100
            : 0;
        },

        isLastQuestion() {
          return (
            this.currentQuestionId !== null &&
            this._getNextQuestionId(this.currentQuestionId) === null
          );
        },

        selectAnswer(questionId, value, skipQuestionsStr, optionBlockId) {
          this.answers.set(questionId, value);
          const skipArr = skipQuestionsStr
            ? skipQuestionsStr
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
          this.skips.set(questionId, skipArr);
          if (optionBlockId)
            this.selectedOptions.set(questionId, optionBlockId);
          const pos = this.questionIds.indexOf(questionId);
          for (let i = pos + 1; i < this.questionIds.length; i++) {
            this.answers.delete(this.questionIds[i]);
            this.skips.delete(this.questionIds[i]);
            this.selectedOptions.delete(this.questionIds[i]);
          }
        },

        hasCurrentAnswer() {
          return this.answers.has(this.currentQuestionId);
        },

        hasAnswer(questionId, value) {
          if (!this.answers) return false;
          const curr = this.answers.get(questionId);
          return JSON.stringify(curr) === JSON.stringify(value);
        },

        getAnswer(questionId) {
          return this.answers.get(questionId);
        },

        _getAllAnswers() {
          return Array.from(this.answers.values()).flatMap((value) => value);
        },

        _filterAnswers(answers) {
          const scores = new Map();
          const exclude = new Set();
          for (const answer of answers) {
            for (const [key, ids] of Object.entries(answer)) {
              const numKey = Number(key);
              for (const id of ids) {
                if (numKey === 0) {
                  exclude.add(id);
                } else {
                  scores.set(id, (scores.get(id) ?? 0) + numKey);
                }
              }
            }
          }
          const sorted = Array.from(scores.entries())
            .filter(([id]) => !exclude.has(id))
            .sort(([, a], [, b]) => b - a);
          const cutoff = sorted[maxProduct - 1]?.[1];
          return sorted.filter(
            ([, score], i) => i < maxProduct || score === cutoff
          );
        },

        _collectResultHtml() {
          const allAnswered = [
            ...this.traversed,
            this.currentQuestionId,
          ].filter(Boolean);
          const items = allAnswered
            .map((qBlockId) => {
              const optBlockId = this.selectedOptions.get(qBlockId);

              const descEl = document.getElementById(`option-description-${optBlockId}`);
              const html = descEl?.innerHTML?.trim() ?? "";

              if (html) return /<li[\s>]/i.test(html) ? html : `<li>${html}</li>`;

              const headingEl = document.getElementById(`option-heading-${optBlockId}`);
              const text = headingEl?.textContent?.trim() ?? "";

              return text ? `<li>${text}</li>` : "";
            })
            .filter(Boolean);
          return items.length ? `<ul>${items.join("")}</ul>` : "";
        },

        _collectArticles() {
          const allAnswered = [
            ...this.traversed,
            this.currentQuestionId,
          ].filter(Boolean);
          const scores = new Map();
          const nodeById = new Map();
          for (const qBlockId of allAnswered) {
            const optBlockId = this.selectedOptions.get(qBlockId);
            if (!optBlockId) continue;
            const container = document.getElementById(
              `option-articles-${optBlockId}`
            );
            if (!container) continue;
            container
              .querySelectorAll(".recommended-article")
              .forEach((el) => {
                const id = el.dataset.articleId;
                if (!id) return;
                scores.set(id, (scores.get(id) ?? 0) + 1);
                if (!nodeById.has(id)) nodeById.set(id, el);
              });
          }
          const sorted = Array.from(scores.entries())
            .sort(([, a], [, b]) => b - a)
            .slice(0, maxArticle);
          const des = document.getElementById(
            `quiz-answer-article-list-${sectionId}`
          );
          if (des) {
            des.innerHTML = "";
            sorted.forEach(([id]) => {
              const node = nodeById.get(id);
              if (node) des.appendChild(node.firstElementChild.cloneNode(true));
            });
          }
          this.hasArticles = sorted.length > 0;
        },

        _fetchRecommendedProduct() {
          this.loading = true;
          this._collectArticles();
          const resultHtml = this._collectResultHtml();
          let productEntries = this._filterAnswers(this._getAllAnswers());
          const productIds = [
            ...new Set(
              productEntries.length > 0
                ? productEntries.map(([id]) => id)
                : defaultProductIds
            ),
          ];

          if (productIds.length === 0) {
            this.hasProducts = false;
            this.curIndex = this.totalQuestions + 1;
            this.currentQuestionId = null;
            this.loading = false;
            return;
          }

          let query = productIds.map((value) => "id:" + value).join(" OR ");
          const searchUrl = `${Shopify.routes.root}search?section_id=${sectionId}&type=product&q=${query}`;

          fetch(searchUrl)
            .then((response) => {
              if (!response.ok) throw new Error(response.status);
              return response.text();
            })
            .then((text) => {
              const html = new DOMParser().parseFromString(text, "text/html");
              const src = html.getElementById("quiz-answer-product-list");
              const des = document.getElementById(
                `quiz-answer-product-list-${sectionId}`
              );
              if (src && des) {
                const idToScore = new Map(
                  productEntries.map(([id, score]) => [String(id), score])
                );
                Array.from(src.children)
                  .sort(
                    (a, b) =>
                      (idToScore.get(b.id) ?? 0) - (idToScore.get(a.id) ?? 0)
                  )
                  .forEach((el) => {
                    src.appendChild(el);
                  });
                des.innerHTML = src.innerHTML;
              }
            })
            .catch((error) => {
              throw error;
            })
            .finally(() => {
              const textContainer = document.getElementById(
                `quiz-answer-text-${sectionId}`
              );
              if (textContainer) textContainer.innerHTML = resultHtml;
              this.curIndex = this.totalQuestions + 1;
              this.currentQuestionId = null;
              this._scrollToTop();
              this.loading = false;
            });
        },

        reset() {
          this.curIndex = 0;
          this.hasProducts = true;
          this.hasArticles = false;
          this.traversed = [];
          this.currentQuestionId = null;
          this.answers = new Map();
          this.skips = new Map();
          this.selectedOptions = new Map();
          const articleList = document.getElementById(
            `quiz-answer-article-list-${sectionId}`
          );
          if (articleList) articleList.innerHTML = "";
          this._scrollToTop();
        },

        _scrollToTop() {
          setTimeout(() => {
            document
              .getElementById(`shopify-section-${sectionId}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 500);
        },

        next() {
          if (this.curIndex === 0) {
            const firstId = this._getNextQuestionId(null);
            if (firstId) {
              this.currentQuestionId = firstId;
              this.curIndex = 1;
              this._scrollToTop();
            } else {
              this._fetchRecommendedProduct();
            }
            return;
          }
          const nextId = this._getNextQuestionId(this.currentQuestionId);
          if (nextId) {
            this.traversed.push(this.currentQuestionId);
            this.currentQuestionId = nextId;
            this.curIndex += 1;
            this._scrollToTop();
          } else {
            this._fetchRecommendedProduct();
          }
        },

        prev() {
          if (this.traversed.length === 0) return;
          this.currentQuestionId = this.traversed.pop();
          this.curIndex -= 1;
          this._scrollToTop();
        },
      }));
    });
  });
}
